/**
 * TagsDialog — drawer de marcação de tags de um contato.
 *
 * ## O que mudou em 22/09/2026
 *
 * Antes, a lista de "Tags Disponíveis" vinha de um `distinct` sobre os
 * contatos: só existia tag que alguém já tivesse marcado em alguém. A
 * consequência era que **não havia como cadastrar uma tag antes de usá-la** —
 * um gestor que quisesse preparar o vocabulário da equipe não tinha por onde,
 * e o botão "Criar Nova Tag" daqui só adicionava a palavra à seleção local,
 * sem nada persistir até alguém salvar o contato.
 *
 * Agora a lista vem do CATÁLOGO (`GET /api/tags/dropdown`), com cor de
 * verdade, e "Criar Nova Tag" cadastra no catálogo na hora — a tag passa a
 * existir para o chat, o CRM, os tickets e as automações, mesmo que este
 * contato não seja salvo.
 *
 * ## Três permissões, três papéis diferentes
 *
 * - `contacts:update` — marcar/desmarcar este contato. Sem ela o drawer nem
 *   abre (é um dos três gates travados por `tests/unit/tags-permission-gates`).
 * - `tags:create` — cadastrar palavra nova no vocabulário da empresa. Sem
 *   ela some o botão de criar, e sobra o seletor.
 * - A cor não é editável aqui: isso é administração do catálogo, e mora em
 *   Configurações → Tags.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Input } from '@/shared/components/ui';
import { useContactTags, useUpdateContactTags } from '../hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { useTagCatalog, tagStyleFrom } from '@/shared/hooks/useTagCatalog';
import { useCreateTag } from '@/modules/tags/hooks/useTags';
import { Icon } from '@/shared/icons'
interface TagsDialogProps {
  contactId: string;
  contactName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const TagsDialog: React.FC<TagsDialogProps> = ({
  contactId,
  contactName,
  isOpen,
  onClose,
}) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [showCreateNew, setShowCreateNew] = useState(false);

  // Buscar tags atuais do contato (somente quando o dialog está aberto).
  // SEM default `= []` no destructuring: com `= []`, cada render cria um array novo
  // (quando data é undefined), o que faz o useEffect abaixo disparar a cada render
  // → setState → loop infinito ("Maximum update depth"). React Query retorna a MESMA
  // referência quando data existe, então depender de `currentTags` direto é estável.
  const { data: currentTags, isLoading: isLoadingContact } = useContactTags(isOpen ? contactId : undefined);

  // Catálogo do tenant — com COR. Condicionado a `isOpen` (CLAUDE.md SEMPRE #10).
  const { data: catalog, isLoading: isLoadingCatalog } = useTagCatalog(isOpen);

  // Mutation para atualizar tags
  const updateTagsMutation = useUpdateContactTags();
  const createTagMutation = useCreateTag();

  // C5 — o diálogo é 100% de edição. Sem `contacts:update` a recusa só chegava
  // como 403 depois de o usuário mexer em tudo. Terceiro dos três gates (os
  // outros dois: coluna de tags do header e botão do painel direito).
  const { hasModulePermission } = usePermissions();
  const canUpdateContacts = hasModulePermission('contacts', 'update');
  const canCreateTags = hasModulePermission('tags', 'create');

  // Inicializar selectedTags quando currentTags estiver disponível (só com o dialog aberto)
  useEffect(() => {
    if (isOpen && currentTags) {
      setSelectedTags(currentTags);
    }
  }, [isOpen, currentTags]);

  const availableTags = useMemo(() => catalog ?? [], [catalog]);

  // Filtrar tags disponíveis por busca
  const filteredTags = useMemo(() => {
    if (!searchQuery) return availableTags;
    const alvo = searchQuery.toLowerCase();
    return availableTags.filter((tag) => tag.name.toLowerCase().includes(alvo));
  }, [availableTags, searchQuery]);

  /**
   * Tags que o contato tem mas que não estão no catálogo — órfãs.
   *
   * Aparecem numa seção própria em vez de sumirem. Órfã acontece de verdade em
   * dois momentos: entre o deploy e a migração do catálogo, e depois de alguém
   * excluir uma tag sem remover as marcações. Escondê-las faria a pessoa achar
   * que a tag foi apagada do contato quando ela continua lá — e continua
   * pesando nos filtros.
   */
  const orphanTags = useMemo(() => {
    const noCatalogo = new Set(availableTags.map((t) => t.name));
    return selectedTags.filter((t) => !noCatalogo.has(t));
  }, [selectedTags, availableTags]);

  const isTagSelected = (tagName: string): boolean => selectedTags.includes(tagName);

  const toggleTag = (tagName: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagName)
        ? prev.filter((t) => t !== tagName)
        : [...prev, tagName]
    );
  };

  /**
   * Cadastra no CATÁLOGO e já marca no contato.
   *
   * Antes esta função só empurrava a string para a seleção local — a tag não
   * existia para mais ninguém até alguém salvar o contato, e se o salvamento
   * fosse cancelado ela sumia. Agora o cadastro é o efeito principal: a
   * palavra passa a existir para toda a empresa.
   */
  const handleCreateNewTag = async () => {
    const trimmedTag = newTagName.replace(/\s+/g, ' ').trim();
    if (!trimmedTag) return;

    try {
      const criada = await createTagMutation.mutateAsync({ name: trimmedTag });
      // Usa o nome CANÔNICO devolvido pelo backend, não o digitado.
      if (!selectedTags.includes(criada.name)) {
        setSelectedTags((prev) => [...prev, criada.name]);
      }
      setNewTagName('');
      setShowCreateNew(false);
    } catch {
      // O toast do hook já explicou (inclusive o 409 de nome já existente).
    }
  };

  // Salvar alterações
  const handleSave = async () => {
    try {
      await updateTagsMutation.mutateAsync({
        contactId,
        tags: selectedTags,
      });

      onClose();
    } catch (error) {
      // Erro já tratado no hook
      console.error('Erro ao salvar tags:', error);
    }
  };

  // Resetar ao fechar
  const handleClose = () => {
    setSelectedTags(currentTags || []);
    setSearchQuery('');
    setNewTagName('');
    setShowCreateNew(false);
    onClose();
  };

  const isLoading = isLoadingContact || isLoadingCatalog;
  const isSaving = updateTagsMutation.isPending;

  // Footer com botões Cancelar + Salvar
  const footerContent = (
    <div className="flex items-center justify-end gap-3 w-full">
      <button
        onClick={handleClose}
        className="px-4 py-2 rounded-lg transition-colors border"
        style={{
          color: '#1e293b',
          backgroundColor: '#ffffff',
          borderColor: '#e5e7eb',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f1f5f9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#ffffff';
        }}
        disabled={isSaving}
      >
        Cancelar
      </button>
      <button
        onClick={handleSave}
        disabled={isSaving || isLoading}
        data-testid="tags-dialog-save"
        className="px-6 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={{ backgroundColor: '#8b5cf6' }}
        onMouseEnter={(e) => {
          if (!isSaving && !isLoading) {
            e.currentTarget.style.backgroundColor = '#7c3aed';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#8b5cf6';
        }}
      >
        {isSaving ? 'Salvando...' : 'Salvar Tags'}
      </button>
    </div>
  );

  // Depois de TODOS os hooks — regra dos hooks do React.
  if (!canUpdateContacts) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      icon={<Icon name="tag" className="h-5 w-5" />}
      title="Gerenciar Tags"
      subtitle={contactName}
      footer={footerContent}
      disabled={isSaving}
    >
      {/* `tags-dialog-sheet` é MARCADOR, não estilo: no celular o `index.css` usa
          `.ds-drw:has(.tags-dialog-sheet)` pra ancorar ESTE drawer embaixo (folha),
          sem tocar no componente shared (que não expõe `className`). */}
      <div className="px-6 py-5 tags-dialog-sheet" data-testid="tags-dialog">
      {/* Selected Tags Preview */}
      {selectedTags.length > 0 && (
        <div
          className="mb-4 p-3 rounded-lg border"
          style={{
            backgroundColor: '#f1f5f9',
            borderColor: '#e5e7eb',
          }}
        >
          <p
            className="text-xs font-medium mb-2"
            style={{ color: '#1e293b' }}
          >
            Tags Selecionadas ({selectedTags.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tagName) => {
              const estilo = tagStyleFrom(catalog, tagName);

              return (
                <span
                  key={tagName}
                  data-testid={`tags-dialog-selected-${tagName}`}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border"
                  style={estilo}
                >
                  {tagName}
                  <button
                    onClick={() => toggleTag(tagName)}
                    data-testid={`tags-dialog-remove-${tagName}`}
                    aria-label={`Remover tag ${tagName}`}
                    className="rounded-full p-0.5 transition-colors hover:bg-black hover:bg-opacity-10"
                  >
                    <Icon name="x" className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>

          {orphanTags.length > 0 && (
            <p className="text-[10px] mt-2" style={{ color: '#92400e' }} data-testid="tags-dialog-orphans">
              {orphanTags.length === 1
                ? `A tag "${orphanTags[0]}" não está no catálogo da empresa.`
                : `${orphanTags.length} tags deste contato não estão no catálogo da empresa.`}{' '}
              Continuam valendo nos filtros, mas não aparecem na lista abaixo.
            </p>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div
            className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: '#8b5cf6' }}
          />
        </div>
      )}

      {!isLoading && (
        <>
          {/* Search Bar */}
          <div className="mb-4">
            <Input
              icon={'search'}
              iconPosition="left"
              placeholder="Buscar tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="tags-dialog-search"
            />
          </div>

          {/* Create New Tag Button */}
          {!showCreateNew && canCreateTags && (
            <button
              onClick={() => setShowCreateNew(true)}
              data-testid="tags-dialog-create"
              className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg transition-colors"
              style={{
                borderColor: '#e5e7eb',
                color: '#64748b',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#8b5cf6';
                e.currentTarget.style.color = '#8b5cf6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              <Icon name="plus" className="w-4 h-4" />
              <span className="text-sm font-medium">Criar Nova Tag</span>
            </button>
          )}

          {/* Create New Tag Form */}
          {showCreateNew && (
            <div
              className="mb-4 p-4 border rounded-lg"
              style={{
                backgroundColor: '#f1f5f9',
                borderColor: '#e5e7eb',
              }}
            >
              <h4
                className="text-sm font-semibold mb-2"
                style={{ color: '#1e293b' }}
              >
                Nova Tag
              </h4>
              <div className="flex gap-2">
                <Input
                  placeholder="Nome da tag..."
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void handleCreateNewTag()}
                  maxLength={60}
                  autoFocus
                />
                <button
                  onClick={() => void handleCreateNewTag()}
                  disabled={!newTagName.trim() || createTagMutation.isPending}
                  className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  style={{ backgroundColor: '#8b5cf6' }}
                  onMouseEnter={(e) => {
                    if (newTagName.trim()) {
                      e.currentTarget.style.backgroundColor = '#7c3aed';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#8b5cf6';
                  }}
                >
                  {createTagMutation.isPending ? 'Criando...' : 'Criar'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateNew(false);
                    setNewTagName('');
                  }}
                  className="px-4 py-2 rounded-lg transition-colors flex-shrink-0"
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#1e293b',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                  }}
                >
                  Cancelar
                </button>
              </div>
              <p className="text-[10px] mt-2" style={{ color: '#64748b' }}>
                A tag passa a existir para toda a empresa — chat, CRM, tickets e automações.
              </p>
            </div>
          )}

          {/* Available Tags List */}
          <div className="space-y-2">
            <h4
              className="text-sm font-semibold mb-3"
              style={{ color: '#1e293b' }}
            >
              Tags Disponíveis
            </h4>

            {filteredTags.length === 0 ? (
              <div
                className="text-center py-8"
                style={{ color: '#64748b' }}
              >
                <Icon name="tag" className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {searchQuery ? 'Nenhuma tag encontrada' : 'Nenhuma tag cadastrada'}
                </p>
                <p className="text-xs mt-1">
                  {canCreateTags
                    ? 'Clique em "Criar Nova Tag" para adicionar'
                    : 'Peça a um administrador para cadastrar em Configurações → Tags'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {filteredTags.map((tag) => {
                  // `tagName` em variável (e não `tag.name` inline) porque o
                  // contrato de testids é travado por leitura do FONTE em
                  // `tests/unit/tags-permission-gates.test.ts` — ele procura
                  // pelo literal `tags-dialog-available-${tagName}`.
                  const tagName = tag.name;
                  const selected = isTagSelected(tagName);
                  const estilo = tagStyleFrom(catalog, tagName);

                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tagName)}
                      data-testid={`tags-dialog-available-${tagName}`}
                      aria-pressed={selected}
                      className="flex items-center justify-between px-3 py-2 rounded-lg border-2 transition-all"
                      style={{
                        borderColor: selected ? '#8b5cf6' : '#e5e7eb',
                        backgroundColor: selected ? 'rgba(139, 92, 246, 0.1)' : '#ffffff',
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      <span
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
                        style={estilo}
                      >
                        {tagName}
                      </span>
                      {selected && (
                        <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
      </div>
    </Drawer>
  );
};

export default TagsDialog;
